import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  FolderIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  BriefcaseIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, teamsAPI } from '../services/api';
import UserTaskModal from '../components/user/UserTaskModal';
import { Task } from '../types';
import toast from 'react-hot-toast';

interface TeamInsight {
  id: string;
  name: string;
  description?: string;
  color: string;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
  };
  members: Array<{
    user: {
      id: string;
      firstName: string;
      lastName: string;
      avatar?: string;
    };
  }>;
  _count: {
    projects: number;
  };
  insights: {
    totalTasks: number;
    userTasks: number;
    completedTasks: number;
    completionRate: number;
    isManager: boolean;
  };
}



const Teams: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<TeamInsight[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamInsight | null>(null);
  const [teamTasks, setTeamTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(false);

  useEffect(() => {
    loadTeams();
  }, [user]);

  const loadTeams = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const teamsData = await usersAPI.getUserTeamInsights(user.id);
      setTeams(teamsData);
      
      if (teamsData.length > 0) {
        setSelectedTeam(teamsData[0]);
        loadTeamData(teamsData[0].id);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeamData = async (teamId: string) => {
    try {
      setIsLoadingTeamData(true);
      const tasksData = await teamsAPI.getTeamTasks(teamId);
      
      const userTasks = tasksData.filter((task: any) =>
        task.assignees.some((assignee: any) => assignee.user.id === user?.id)
      );
      setTeamTasks(userTasks as Task[]);
    } catch (error) {
      console.error('Error loading team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setIsLoadingTeamData(false);
    }
  };

  const handleTeamSelect = (team: TeamInsight) => {
    setSelectedTeam(team);
    loadTeamData(team.id);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };

  const handleTaskUpdate = (updatedTask: any) => {
    setTeamTasks(prev => prev.map(t => t.id === updatedTask.id ? updatedTask : t));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'BLOCKED':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-500';
      case 'HIGH':
        return 'bg-orange-500';
      case 'MEDIUM':
        return 'bg-yellow-500';
      case 'LOW':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
              <UserGroupIcon className="w-8 h-8 text-primary-600" />
              <span>My Teams</span>
            </h1>
            <p className="text-gray-600 mt-1">
              View your teams, projects, and assigned tasks
            </p>
          </div>
        </div>

        {teams.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams found</h3>
            <p className="mt-1 text-sm text-gray-500">
              You are not currently a member of any teams. Contact your administrator to be added to teams.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Teams ({teams.length})</h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {teams.map((team) => (
                    <motion.div
                      key={team.id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className={`p-4 cursor-pointer transition-colors ${
                        selectedTeam?.id === team.id ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                      }`}
                      onClick={() => handleTeamSelect(team)}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: team.color }}
                        >
                          <UserGroupIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                              {team.name}
                            </h3>
                            {team.insights.isManager && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Manager
                              </span>
                            )}
                          </div>
                          {team.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {team.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-3 text-xs text-gray-500">
                              <span className="flex items-center">
                                <UserIcon className="w-3 h-3 mr-1" />
                                {team.members.length}
                              </span>
                              <span className="flex items-center">
                                <FolderIcon className="w-3 h-3 mr-1" />
                                {team._count.projects}
                              </span>
                              <span className="flex items-center">
                                <ClockIcon className="w-3 h-3 mr-1" />
                                {team.insights.userTasks}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              {selectedTeam ? (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-16 h-16 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: selectedTeam.color }}
                        >
                          <UserGroupIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{selectedTeam.name}</h2>
                          {selectedTeam.description && (
                            <p className="text-gray-600 mt-1">{selectedTeam.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span>Manager: {selectedTeam.manager.firstName} {selectedTeam.manager.lastName}</span>
                            <span>•</span>
                            <span>{selectedTeam.members.length} members</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <BriefcaseIcon className="w-5 h-5 text-blue-600" />
                          <span className="ml-2 text-sm font-medium text-blue-900">Projects</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900 mt-1">{selectedTeam._count.projects}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <ClockIcon className="w-5 h-5 text-green-600" />
                          <span className="ml-2 text-sm font-medium text-green-900">My Tasks</span>
                        </div>
                        <p className="text-2xl font-bold text-green-900 mt-1">{selectedTeam.insights.userTasks}</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                          <span className="ml-2 text-sm font-medium text-purple-900">Completed</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-900 mt-1">{selectedTeam.insights.completedTasks}</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="flex items-center">
                          <ArrowTrendingUpIcon className="w-5 h-5 text-orange-600" />
                          <span className="ml-2 text-sm font-medium text-orange-900">Progress</span>
                        </div>
                        <p className="text-2xl font-bold text-orange-900 mt-1">
                          {Math.round(selectedTeam.insights.completionRate)}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {isLoadingTeamData ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <ClockIcon className="w-5 h-5 mr-2 text-primary-600" />
                          My Tasks ({teamTasks.length})
                        </h3>
                      </div>
                      <div className="p-4">
                        {teamTasks.length === 0 ? (
                          <div className="text-center py-8">
                            <ClockIcon className="mx-auto h-8 w-8 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">No tasks assigned to you in this team</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {teamTasks.slice(0, 10).map((task) => (
                              <motion.div
                                key={task.id}
                                whileHover={{ scale: 1.02 }}
                                className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                onClick={() => handleTaskClick(task)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                                      <h4 className="text-sm font-medium text-gray-900 line-clamp-1">{task.title}</h4>
                                      {isOverdue(task.dueDate) && task.status !== 'DONE' && (
                                        <ExclamationTriangleIcon className="w-4 h-4 text-red-500" title="Overdue" />
                                      )}
                                    </div>
                                    {task.project && (
                                      <div className="flex items-center space-x-1 mt-1">
                                        <div
                                          className="w-2 h-2 rounded-full"
                                          style={{ backgroundColor: task.project.color }}
                                        />
                                        <span className="text-xs text-gray-500">{task.project.name}</span>
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between mt-2">
                                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(task.status)}`}>
                                        {task.status.replace('_', ' ')}
                                      </span>
                                      {task.dueDate && (
                                        <span className="text-xs text-gray-500">
                                          Due: {new Date(task.dueDate).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a team</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a team from the list to view its projects and your assigned tasks.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <UserTaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdate={handleTaskUpdate}
        />
      </div>
    </Layout>
  );
};

export default Teams;
